import React, { useState } from 'react';
import { Button, Notice, Text } from '@heartinternet/hi-ui';
import type { SSLInfoContext } from '../SSLInfoPage';
import type { OrgFormData, SSLInfoData } from '../types';
import {
  buildAssignPayload,
  buildCommonName,
  endpoints,
  getCertificateName,
  requiresOrgDetails,
  rpcPost,
} from '../helpers';
import { DomainSection, type DomainValue } from './DomainSection';
import { OrgDetailsForm } from './OrgDetailsForm';
import { TermsSection } from './TermsSection';

export interface SubmissionFormProps {
  info: SSLInfoData;
  ctx: SSLInfoContext;
  sslIssuer: string;
}

const EMPTY_FORM: OrgFormData = {
  organisation: null,
  jobTitle: null,
  address1: null,
  address2: null,
  postcode: null,
  city: null,
  state: null,
  country: null,
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
};

/** Install/Assign flow (status NEW) — mirrors submission/page-submission.js. */
export const SubmissionForm: React.FC<SubmissionFormProps> = ({ info, ctx, sslIssuer }) => {
  const productName = getCertificateName(info.product_type, info.is_wildcard);
  const needsOrg = requiresOrgDetails(info.product_type);
  const isEV = info.product_type === 'Extended';

  const [domain, setDomain] = useState<DomainValue>({
    domainName: null,
    domainOid: null,
    subdomain: info.is_wildcard ? '*' : '',
    csr: null,
  });
  const [form, setForm] = useState<OrgFormData>(EMPTY_FORM);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [domainValid, setDomainValid] = useState(false);
  const [orgValid, setOrgValid] = useState(false);

  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const allValid = domainValid && (!needsOrg || orgValid) && termsAccepted;

  const patchDomain = (patch: Partial<DomainValue>) => setDomain((d) => ({ ...d, ...patch }));
  const patchForm = (patch: Partial<OrgFormData>) => setForm((f) => ({ ...f, ...patch }));

  const handleAssign = async () => {
    setAssignError(null);
    if (!allValid) {
      setShowErrors(true);
      setAssignError('Please complete all highlighted sections before assigning the SSL.');
      return;
    }
    /* v8 ignore start -- unreachable when allValid (domainValid implies a domain name); kept for the type narrow */
    if (!domain.domainName) return;
    /* v8 ignore stop */

    setSubmitting(true);
    const commonName = buildCommonName(domain.csr, domain.subdomain, domain.domainName);
    const payload = buildAssignPayload({
      commonName,
      csr: domain.csr,
      installTo: domain.domainOid,
      form,
    });
    const res = await rpcPost(ctx.fetchFn, endpoints.assign(ctx.baseUrl, ctx.oid), payload);
    setSubmitting(false);
    if (res.error) {
      setAssignError(res.error);
      return;
    }
    ctx.refetch();
  };

  /* v8 ignore next -- a NEW cert always includes assignable_hosting_packages; [] guards its absence */
  const packages = info.assignable_hosting_packages ?? [];

  return (
    <div className="hi-ui-ssl-info__flow">
      <Text variant="heading-2">Install {productName} SSL</Text>
      <Text variant="body">
        Complete the following sections in your application for an SSL.
        {needsOrg &&
          ' Information included in the organisation details section will be used to verify and validate your certificate.'}
      </Text>

      <DomainSection
        packages={packages}
        isWildcard={info.is_wildcard}
        value={domain}
        onChange={patchDomain}
        onValidChange={setDomainValid}
        onCsrOrgData={patchForm}
        baseUrl={ctx.baseUrl}
        fetchFn={ctx.fetchFn}
        invalid={showErrors && !domainValid}
      />

      {needsOrg && (
        <OrgDetailsForm
          isEV={isEV}
          value={form}
          onChange={patchForm}
          onValidChange={setOrgValid}
          showErrors={showErrors}
          invalid={showErrors && !orgValid}
          idealPostcodesApiKey={ctx.idealPostcodesApiKey}
        />
      )}

      <TermsSection
        sslIssuer={sslIssuer}
        termsUrl={ctx.termsUrl}
        checked={termsAccepted}
        onChange={setTermsAccepted}
        invalid={showErrors && !termsAccepted}
      />

      {assignError && <Notice variant="error">{assignError}</Notice>}

      <div className="hi-ui-ssl-info__actions">
        <Button variant="primary" loading={submitting} onClick={handleAssign}>
          Assign
        </Button>
      </div>
    </div>
  );
};
